import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CjsSmartSelect, SmartSelectOptions } from "../../components/smartSelect/smartSelect";
import { EDITION_CANCELLED_EVENT, SHOULD_FINISH_ON_ENTER_FUNCTION } from "../../components/smartTable/smartTableEdition.helper";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

export type SelectEditorEditorOptions = EditorOptions;

export const defaultSelectEditorOptions: SelectEditorEditorOptions = {
    ...defaultEditorOptions
};

/* Group options of editor himself and select */
export type SelectEditorOptions<ItemType, ValueType> = {
    options?: Partial<SelectEditorEditorOptions>,
    smartSelectOptions?: Partial<SmartSelectOptions<ItemType, ValueType>>
};

@CustomElement({
    name: "cjs-select-editor",
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsSelectEditor<ItemType, ValueType> extends CjsEditor<ValueType> {

    @TypedWebComponentOptions(defaultSelectEditorOptions)
    public readonly options!: WebComponentOptions<SelectEditorEditorOptions>;

    /** Direct access to smartSelect options */
    public get smartSelectOptions(): WebComponentOptions<SmartSelectOptions<ItemType, ValueType>> {
        return this.smartSelect.options;
    }

    /** SmartSelect fo selectEditor */
    public smartSelect = new CjsSmartSelect<ItemType, ValueType>();

    constructor(options?: Partial<SelectEditorOptions<ItemType, ValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        this.options.setWithoutRefresh(options?.options);
        this.smartSelectOptions.setWithoutRefresh({
            popup: {
                doNotCloseOnEnterKey: true,// this is needed to be able to select first filtered item on enter key pressed,
                onPopupCancel: () => {
                    this.dispatchEvent(new Event(EDITION_CANCELLED_EVENT));
                }
            },
            ...options?.smartSelectOptions
        });
    }

    /** Return component input */
    public getInput(): CjsSmartSelect<ItemType, ValueType> {
        return this.smartSelect;
    }

    /** When component is initialize */
    public override init(): void {
        super.init();

        // Should add smartSelect dynamically
        this.smartSelect.classList.add(FLEX_CONTAINER, FLEX_ITEM_AUTO);

        this.smartSelect.selectEvent.on(() => this._onInputValueChange());
        this.smartSelect.selectAllEvent.on(() => this._onInputValueChange());
        this.smartSelect.unselectAllEvent.on(() => this._onInputValueChange());

        // Add smartSelect to selectEditor
        this.prepend(this.smartSelect);
    }

    /**
     * Function that refresh the displayed selectEditor
     */
    public override refresh(): void {
        super.refresh();

        /** Update value from selected value if value isn't defined */
        if (this._value !== this.smartSelectOptions.selectedValue && this.smartSelectOptions.selectedValue != null) {
            this._value = this.smartSelectOptions.selectedValue;
        }

        if (this.smartSelect.isInitDone) {
            // if !isInitDone refresh will be called later
            this.smartSelect.refresh();
        }
    }


    /** Open the select if needed than trigger onDone when value has changed */
    public override tryEdit(onDone: () => void): void {
        // Register callback when value has changed
        this.changeEvent.on(onDone);// Change event callback triggered when user click on value

        // Open the select if needed for direct access to the selection
        if (!this.smartSelect.isOpen()) {
            this.smartSelect.open();
        }
    }

    /**
     * @returns validationFormElement
     * Where "is-invalid" class will be added / removed
     */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().components.smartSelectContainer;
    }

    /**
     * Update the input value from stored value
     */
    protected override _refreshInputValue(): void {
        // Set selected value option to editor
        this.smartSelectOptions.setAndRefreshIfChanged({ selectedValue: this._value });
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): ValueType | undefined {
        return this.smartSelectOptions.selectedValue ?? undefined;
    }

    /** Update disabled attribute on input component */
    protected override _refreshDisabled(): void {
        this.smartSelectOptions.setWithoutRefresh({
            disabled: this.disabled
        });
    }

    /**
     * This function is used by SmartTableEditionHelper in order to prevent the smart table from finishing edition on enter
     */
    public shouldSmartTableFinishValueEditionOnEnter(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const refToCode = SHOULD_FINISH_ON_ENTER_FUNCTION;// this variable is here only to link to the code that uses this function
        return false;
    }
}